<template>
  <v-app id="app_outlets_list">
    <v-container fluid>
      <v-row class="pl-2 pr-6">
        <v-col class="mt-3" cols="7">
          <p class="custom_page_main_title">DEALER LIST</p>
          <p class="custom_page_main_sub_title" style="opacity: 0.6">Analyse the created outlet list using a variety of parameters.</p>
        </v-col>
        <v-col class="px-4 mt-5" cols="5">
          <v-row class="custom_tool_bar_style">
            <v-divider :thickness="3" class="divider-vertical" vertical></v-divider>
            <v-btn class="gray-text" size="x-large" variant="text" @click="backPageRoute()">
              <v-icon size="x-large">mdi-menu-left</v-icon> BACK
            </v-btn>
          </v-row>
        </v-col>
      </v-row>

      <v-card class="custom_card_box mt-8" elevation="1" border="sm opacity-1" rounded="lg">
        <v-card-text class="custom_tool_bar_style d-flex align-center">
          <v-btn class="custom_pdf_btn" variant="outlined" @click="exportPDF">
            <v-icon size="large">mdi-file-pdf</v-icon> PDF
          </v-btn>
          <v-btn class="custom_excel_btn ml-4" variant="outlined" @click="exportExcel">
            <v-icon size="large">mdi-file-excel</v-icon> EXCEL
          </v-btn>
          <!-- Delete All button - only visible for administrators -->
        </v-card-text>

        <v-divider thickness="2"></v-divider>

        <v-card-text>
          <v-row class="mt-0">
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="selectedDealerCodes"
                :items="dealerCodeOptions"
                label="Dealer Code"
                placeholder="Select dealer code"
                variant="outlined"
                density="comfortable"
                class="custom_input_field_color"
                multiple
                chips
                clearable
                :loading="loading"
                :disabled="loading || dealerCodeOptions.length === 0"
                @update:modelValue="handleFilterChange"
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip v-if="index === 0" size="small" color="primary" variant="flat">
                    <span>{{ item?.title }}</span>
                  </v-chip>
                  <span v-if="index === 1" class="text-grey text-caption ml-1">
                    (+{{ selectedDealerCodes.length - 1 }})
                  </span>
                </template>
                <template v-slot:prepend-inner>
                  <v-icon size="small">mdi-code-tags</v-icon>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="selectedDealerNames"
                :items="dealerNameOptions"
                label="Dealer Name"
                placeholder="Select dealer name"
                variant="outlined"
                density="comfortable"
                class="custom_input_field_color"
                multiple
                chips
                clearable
                :loading="loading"
                :disabled="loading || dealerNameOptions.length === 0"
                @update:modelValue="handleFilterChange"
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip v-if="index === 0" size="small" color="primary" variant="flat">
                    <span>{{ item?.title }}</span>
                  </v-chip>
                  <span v-if="index === 1" class="text-grey text-caption ml-1">
                    (+{{ selectedDealerNames.length - 1 }})
                  </span>
                </template>
                <template v-slot:prepend-inner>
                  <v-icon size="small">mdi-store</v-icon>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="globalSearch"
                label="Global Search"
                placeholder="Search by dealer code, dealer name, company name, address, area, mobile number, status..."
                variant="outlined"
                density="comfortable"
                class="custom_input_field_color"
                clearable
                @input="handleFilterChange"
              >
                <template v-slot:prepend-inner>
                  <v-icon size="small" color="primary">mdi-magnify</v-icon>
                </template>
                <template v-slot:append>
                  <v-chip v-if="globalSearch" size="small" color="primary" variant="flat" class="mr-2">
                    <v-icon start size="x-small">mdi-magnify</v-icon>
                    "{{ globalSearch }}"
                  </v-chip>
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <v-row class="mt-2">
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="selectedDealerStatuses"
                :items="dealerStatuses"
                label="Dealer Status"
                placeholder="Select dealer statuses"
                variant="outlined"
                density="comfortable"
                class="custom_input_field_color"
                multiple
                chips
                clearable
                @update:modelValue="handleFilterChange"
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip v-if="index === 0" size="small" color="primary" variant="flat">
                    <span>{{ item?.title }}</span>
                  </v-chip>
                  <span v-if="index === 1" class="text-grey text-caption ml-1">
                    (+{{ selectedDealerStatuses.length - 1 }})
                  </span>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="selectedAreas"
                :items="areas"
                label="Area"
                placeholder="Select areas"
                variant="outlined"
                density="comfortable"
                class="custom_input_field_color"
                multiple
                chips
                clearable
                @update:modelValue="handleFilterChange"
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip v-if="index === 0" size="small" color="primary" variant="flat">
                    <span>{{ item?.title }}</span>
                  </v-chip>
                  <span v-if="index === 1" class="text-grey text-caption ml-1">
                    (+{{ selectedAreas.length - 1 }})
                  </span>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="selectedAssignCompanies"
                :items="assignCompanies"
                label="Assign Company"
                placeholder="Select companies"
                variant="outlined"
                density="comfortable"
                class="custom_input_field_color"
                multiple
                chips
                clearable
                @update:modelValue="handleFilterChange"
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip v-if="index === 0" size="small" color="primary" variant="flat">
                    <span>{{ item?.title }}</span>
                  </v-chip>
                  <span v-if="index === 1" class="text-grey text-caption ml-1">
                    (+{{ selectedAssignCompanies.length - 1 }})
                  </span>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="selectedDealerTypes"
                :items="dealerTypes"
                label="Dealer Type"
                placeholder="Select dealer types"
                variant="outlined"
                density="comfortable"
                class="custom_input_field_color"
                multiple
                chips
                clearable
                @update:modelValue="handleFilterChange"
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip v-if="index === 0" size="small" color="primary" variant="flat">
                    <span>{{ item?.title }}</span>
                  </v-chip>
                  <span v-if="index === 1" class="text-grey text-caption ml-1">
                    (+{{ selectedDealerTypes.length - 1 }})
                  </span>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-row v-if="hasActiveFilters" class="mt-2 mb-2">
            <v-col cols="12">
              <div class="d-flex align-center flex-wrap ga-2">
                <span class="text-caption font-weight-medium">Active Filters:</span>

                <v-chip
                  v-for="code in selectedDealerCodes"
                  :key="'code-' + code"
                  size="x-small"
                  color="primary"
                  variant="flat"
                  closable
                  @click:close="removeDealerCode(code)"
                >
                  Code: {{ code }}
                </v-chip>

                <v-chip
                  v-for="name in selectedDealerNames"
                  :key="'name-' + name"
                  size="x-small"
                  color="primary"
                  variant="flat"
                  closable
                  @click:close="removeDealerName(name)"
                >
                  Name: {{ name }}
                </v-chip>

                <v-chip
                  v-for="status in selectedDealerStatuses"
                  :key="'status-' + status"
                  size="x-small"
                  color="primary"
                  variant="flat"
                  closable
                  @click:close="removeDealerStatus(status)"
                >
                  Status: {{ status }}
                </v-chip>

                <v-chip
                  v-for="area in selectedAreas"
                  :key="'area-' + area"
                  size="x-small"
                  color="success"
                  variant="flat"
                  closable
                  @click:close="removeArea(area)"
                >
                  Area: {{ area }}
                </v-chip>

                <v-chip
                  v-for="company in selectedAssignCompanies"
                  :key="'company-' + company"
                  size="x-small"
                  color="orange"
                  variant="flat"
                  closable
                  @click:close="removeAssignCompany(company)"
                >
                  Company: {{ company }}
                </v-chip>

                <v-chip
                  v-for="type in selectedDealerTypes"
                  :key="'type-' + type"
                  size="x-small"
                  color="purple"
                  variant="flat"
                  closable
                  @click:close="removeDealerType(type)"
                >
                  Type: {{ type }}
                </v-chip>

                <v-btn
                  size="x-small"
                  color="error"
                  variant="text"
                  @click="clearAllFilters"
                  class="ml-2"
                >
                  Clear All
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <v-divider class="mt-4 mb-4"></v-divider>

          <div v-if="loading" class="text-center pa-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <p class="mt-2">Loading dealers...</p>
          </div>

          <v-data-table
            v-else
            :headers="headers"
            :items="paginatedFilteredRoutes"
            :items-per-page="perPage"
            :page="currentPage"
            hide-default-footer
            class="custom_table_normal"
          >
            <template v-slot:[`item.dealer_status`]="{ item }">
              <div class="d-flex align-center justify-space-between ga-2">
                <div class="flex-grow-1">
                  <v-chip
                    v-if="item.dealer_status === 'ALLOWED'"
                    class="custom_green_chip"
                    size="small"
                    variant="flat"
                    style="min-width: 110px;"
                  >
                    <v-icon start size="small">mdi-check</v-icon>
                    Allowed
                  </v-chip>

                  <v-chip
                    v-else-if="item.dealer_status === 'NOT ALLOWED'"
                    class="custom_red_chip"
                    size="small"
                    variant="flat"
                    style="min-width: 110px;"
                  >
                    <v-icon start size="small">mdi-close</v-icon>
                    Not Allowed
                  </v-chip>

                  <v-chip
                    v-else-if="item.dealer_status === 'NEED APPROVAL'"
                    class="custom_blue_chip"
                    size="small"
                    variant="flat"
                    style="min-width: 130px;"
                  >
                    <v-icon start size="small">mdi-help-circle-outline</v-icon>
                    Need Approval
                  </v-chip>

                  <v-chip
                    v-else-if="item.dealer_status"
                    class="mt-2"
                    size="small"
                    variant="flat"
                    color="grey"
                  >
                    {{ item.dealer_status }}
                  </v-chip>

                  <v-chip
                    v-else
                    size="small"
                    variant="flat"
                    color="grey-lighten-2"
                  >
                    No Status
                  </v-chip>
                </div>
              </div>
            </template>

            <template v-slot:[`item.mobile_numbers`]="{ item }">
              <div v-if="item.mobile_number || item.mobile_number2">
                <div class="text-caption" v-if="item.mobile_number">
                  {{ formatMobileNumber(item.mobile_number) }}
                </div>
                <div class="text-caption mt-1" v-if="item.mobile_number2">
                  {{ formatMobileNumber(item.mobile_number2) }}
                </div>
              </div>
              <div v-else>
                <span class="text-grey text-caption">No mobile numbers</span>
              </div>
            </template>

            <template v-slot:[`item.areas`]="{ item }">
              <div>
                <v-chip size="x-small" class="mr-1 mb-1" v-if="item.area_code">
                  {{ item.area_code }}
                </v-chip>
                <span v-if="!item.area_code" class="text-grey text-caption">No area</span>
              </div>
            </template>

            <!-- Updated Assign Company template to show all companies -->
            <template v-slot:[`item.assign_company`]="{ item }">
              <div v-if="item.companies && item.companies.length > 0" class="py-1">
                <div
                  v-for="(company, index) in item.companies"
                  :key="company.id || index"
                  class="company-item mb-1"
                >
                  <v-chip
                    size="x-small"
                    color="primary"
                    variant="flat"
                    class="company-chip"
                  >
                    {{ company.company }}
                  </v-chip>
                  <div class="company-details text-caption mt-1" style="font-size: 10px; color: #666;">
                    <span v-if="company.credit_limit_type || company.cash_limit_type">
                      <span v-if="company.credit_limit_type">Credit: {{ formatCurrency(company.credit_limit_type) }}</span>
                      <span v-if="company.credit_limit_type && company.cash_limit_type" class="mx-1">|</span>
                      <span v-if="company.cash_limit_type">Cash: {{ formatCurrency(company.cash_limit_type) }}</span>
                    </span>
                    <span v-else class="text-grey">No limits set</span>
                  </div>
                  <v-divider v-if="index < item.companies.length - 1" class="my-1"></v-divider>
                </div>
              </div>
              <div v-else class="py-2">
                <span class="text-grey text-caption">No company assigned</span>
              </div>
            </template>

            <template v-slot:[`item.action`]="{ item }">
              <v-menu transition="slide-x-transition" id="app_outlets_list">
                <template v-slot:activator="{ props }">
                  <v-btn
                    style="background-color: #800080; color: white;"
                    v-bind="props"
                    size="small"
                  >
                    ACTIONS <v-icon>mdi-menu-down</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item v-if="permissions.read" @click="viewItem(item)">
                    <v-list-item-title class="custom_font_color_view">VIEW</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="permissions.write" @click="editItem(item)">
                    <v-list-item-title class="custom_font_color_edit">EDIT</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="permissions.write" @click="updateStates(item)">
                    <v-list-item-title class="custom_font_color_edit">STATUS UPDATE</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="permissions.delete" @click="confirmDelete(item)">
                    <v-list-item-title class="custom_font_color_delete">DELETE</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>

            <template v-slot:no-data>
              <div class="text-center pa-4">
                <v-icon size="large" color="grey">mdi-information</v-icon>
                <p class="mt-2">No dealers found matching your filters</p>
                <v-btn
                  variant="text"
                  color="primary"
                  size="small"
                  @click="clearAllFilters"
                  class="mt-2"
                >
                  Clear all filters
                </v-btn>
              </div>
            </template>
          </v-data-table>

          <div class="d-flex justify-space-between align-center mt-4" v-if="!loading">
            <div class="text-caption text-grey">
              Showing {{ paginationStart }} to {{ paginationEnd }} of {{ filteredRoutes.length }} entries
            </div>
            <v-pagination
              v-model="currentPage"
              :length="totalPages"
              :total-visible="7"
              @update:modelValue="handlePageChange"
            ></v-pagination>
            <div class="d-flex align-center">
              <span class="text-caption text-grey mr-2">Rows per page:</span>
              <v-autocomplete
                v-model="perPage"
                :items="[10, 25, 50, 100]"
                variant="outlined"
                density="compact"
                style="width: 100px;"
                hide-details
                @update:modelValue="handlePerPageChange"
              ></v-autocomplete>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <v-dialog v-model="updateDialog" max-height="700" id="app_outlets_list_update" max-width="600">
        <v-card rounded="md" style="overflow:hidden !important;">
          <div class="pa-4">
            <v-card color="#F0F0F0" flat>
              <template v-slot:prepend>
                <v-card-title class="text-subtitle-1 font-weight-bold" v-if="permissions.write">UPDATE STATUS</v-card-title>
              </template>
              <v-spacer></v-spacer>
              <template v-slot:append>
                <v-btn @click="updateDialog = false" icon="mdi-close" size="x-small"></v-btn>
              </template>
            </v-card>

            <v-card flat class="mt-3">
              <v-text-field v-model="updateDetails.dealer_name" label="Dealer Name"
                            readonly variant="outlined" density="comfortable" class="mt-4 custom_input_field_color"
              ></v-text-field>

              <v-text-field v-model="updateDetails.dealer_code" label="Dealer Code"
                            readonly variant="outlined" density="comfortable" class="mt-2 custom_input_field_color"
              ></v-text-field>

              <v-text-field v-model="updateDetails.mobile_number" label="Mobile Number"
                            readonly variant="outlined" density="comfortable" class="mt-2 custom_input_field_color"
              ></v-text-field>

              <v-autocomplete v-model="selectOS" :items="dealerStatuses" label="Dealer Status" variant="outlined" density="comfortable"
                        class="mt-2 custom_input_field_color"
              ></v-autocomplete>

              <v-divider class="mt-4"></v-divider>
              <div class="float-end mt-4">
                <v-btn style="background-color: #800080; color: white;" @click="reminderSend(updateDetails.id)" :loading="statusUpdateLoading">UPDATE</v-btn>
                <v-btn class="ml-3 custom_btn_secondary" @click="updateDialog = false">CANCEL</v-btn>
              </div>
            </v-card>
          </div>
        </v-card>
      </v-dialog>

      <v-dialog v-model="deleteDialog" id="app_outlets_list_delete" max-height="500" max-width="500" persistent>
        <v-card rounded="md">
          <div class="pa-4">
            <v-card color="#F0F0F0" flat>
              <template v-slot:prepend>
                <v-card-title class="text-subtitle-1 font-weight-bold">DELETE DATA</v-card-title>
              </template>
              <v-spacer></v-spacer>
              <template v-slot:append>
                <v-btn @click="deleteDialog = false" icon="mdi-close" size="x-small"></v-btn>
              </template>
            </v-card>

            <v-card color="white" flat class="mt-3">
              <v-card-item>
                <v-card-title style="color: #BB0000" class="text-body-1 font-weight-medium">
                  <v-icon color="warning" size="small">mdi-alert</v-icon>
                  Do you want to delete this record ?
                </v-card-title>

                <p style="opacity: 0.7;" class="text-body-2 mt-4 text-justify">
                  This record will be deleted immediately. Once deleted, it can no longer be used for any purpose.
                </p>

                <v-divider class="mt-4"></v-divider>

                <div class="float-end mt-4">
                  <v-btn class="custom_btn_primary" @click="deleteItem" :loading="deleteLoading" color="error">DELETE</v-btn>
                  <v-btn class="ml-3 custom_btn_secondary" @click="deleteDialog = false">CANCEL</v-btn>
                </div>
              </v-card-item>
            </v-card>
          </div>
        </v-card>
      </v-dialog>

      <v-dialog v-model="deleteAllDialog" id="app_outlets_list_delete_all" max-height="500" max-width="500" persistent>
        <v-card rounded="md">
          <div class="pa-4">
            <v-card color="#F0F0F0" flat>
              <template v-slot:prepend>
                <v-card-title class="text-subtitle-1 font-weight-bold">DELETE ALL DATA</v-card-title>
              </template>
              <v-spacer></v-spacer>
              <template v-slot:append>
                <v-btn @click="deleteAllDialog = false" icon="mdi-close" size="x-small"></v-btn>
              </template>
            </v-card>

            <v-card color="white" flat class="mt-3">
              <v-card-item>
                <v-card-title style="color: #BB0000" class="text-body-1 font-weight-medium">
                  <v-icon color="warning" size="small">mdi-alert</v-icon>
                  Do you want to delete all filtered records?
                </v-card-title>

                <p style="opacity: 0.7;" class="text-body-2 mt-4 text-justify">
                  All currently filtered records will be deleted immediately. Once deleted, they can no longer be used for any purpose.
                </p>

                <v-divider class="mt-4"></v-divider>

                <div class="float-end mt-4">
                  <v-btn class="custom_btn_primary" @click="executeDeleteAll" :loading="deleteAllLoading" color="error" style="border-radius: 2px;">DELETE ALL</v-btn>
                  <v-btn class="ml-3 custom_btn_secondary" @click="deleteAllDialog = false" style="border-radius: 2px;">CANCEL</v-btn>
                </div>
              </v-card-item>
            </v-card>
          </div>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackbar" :color="snackbarColor" location="top right" :timeout="3000">
        {{ snackbarMessage }}
      </v-snackbar>
    </v-container>
  </v-app>
</template>

<script>
import axios from 'axios';
import {mapActions} from "vuex";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import {getPermissions} from "@/plugins/permissions";

export default {
  name: "OutletList",
  data() {
    return {
      baseURL: process.env.VUE_APP_API_BASE_URL,

      snackbarMessage: '',
      snackbarColor: '',
      loading: false,
      deleteLoading: false,
      deleteAllLoading: false,
      statusUpdateLoading: false,
      snackbar: false,
      reason: '',
      deleteDialog: false,
      deleteAllDialog: false,
      updateDialog: false,
      itemToDelete: null,
      showSwitches: false,
      SalesSwitch: false,
      CollectionsSwitch: false,
      selectOS: '',

      selectedDealerCodes: [],
      selectedDealerNames: [],
      selectedDealerStatuses: [],
      selectedAreas: [],
      selectedAssignCompanies: [],
      selectedDealerTypes: [],

      dealerCodeOptions: [],
      dealerNameOptions: [],
      
      // Updated Dealer Types based on the provided image
      dealerTypes: [
        'Furniture & Electronic',
        'Furniture',
        'Electronic',
        'Hardware',
        'Super Center-Daxer',
        'Book Shop',
        'Delivery & Government Suppliers',
        'Non Transaction'
      ],
      
      dealerStatuses: ['ALLOWED', 'NOT ALLOWED'],
      areas: [],
      assignCompanies: [],

      globalSearch: '',

      headers: [
        {title: 'DEALER CODE', key: 'dealer_code', sortable: true},
        {title: 'DEALER NAME', key: 'dealer_name', sortable: true},
        {title: 'COMPANY NAME', key: 'company_name', sortable: true},
        {title: 'MOBILE NUMBERS', key: 'mobile_numbers', sortable: false},
        {title: 'AREA', key: 'areas', sortable: false},
        {title: 'DEALER STATUS', key: 'dealer_status', sortable: true},
        {title: 'ASSIGN COMPANY', key: 'assign_company', sortable: true},
        {title: 'DEALER ADDRESS', key: 'address', sortable: true},
        {title: 'ACTIONS', key: 'action', sortable: false},
      ],

      routes: [],
      updateDetails: {},
      id: '',

      permissions: { read: false, write: false, delete: false },
      isAdministrator: false,

      currentPage: 1,
      perPage: 10,
    };
  },

  methods: {
    ...mapActions(['updateSharedDataOutlet']),

    checkAdministratorStatus() {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        const value = localStorage.getItem(key);

        if (value && value.includes('ADMINISTRATOR')) {
          if (key === 'user') {
            try {
              const parsed = JSON.parse(value);
              if (parsed.userType === 'ADMINISTRATOR') {
                this.isAdministrator = true;
                return;
              }
              if (parsed.user && parsed.user.userType === 'ADMINISTRATOR') {
                this.isAdministrator = true;
                return;
              }
            } catch (e) {
              console.error('Error parsing user key:', e);
            }
          }

          if (key === 'loginResponse') {
            try {
              const parsed = JSON.parse(value);
              if (parsed.user && parsed.user.userType === 'ADMINISTRATOR') {
                this.isAdministrator = true;
                return;
              }
              if (parsed.userType === 'ADMINISTRATOR') {
                this.isAdministrator = true;
                return;
              }
            } catch (e) {
              console.error('Error parsing loginResponse key:', e);
            }
          }

          if (key === 'userType') {
            if (value === 'ADMINISTRATOR') {
              this.isAdministrator = true;
              return;
            }
          }
        }
      }
    },

    // NEW: Central company name mapping helper.
    // Use this everywhere a company name is displayed/filtered so the
    // rename is applied consistently (filter dropdown, table, PDF, Excel).
    normalizeCompanyName(name) {
      if (!name) return name;
      const trimmed = String(name).trim();
      if (trimmed.toLowerCase() === 'd.r.industries electronics') {
        return 'D.R.Industries (Pvt) Ltd';
      }
      return trimmed;
    },

    handleFilterChange() {
      this.currentPage = 1;
      this.applyFilters();
    },

    handlePageChange(page) {
      this.currentPage = page;
    },

    handlePerPageChange() {
      this.currentPage = 1;
    },

    clearAllFilters() {
      this.selectedDealerCodes = [];
      this.selectedDealerNames = [];
      this.selectedDealerStatuses = [];
      this.selectedAreas = [];
      this.selectedAssignCompanies = [];
      this.selectedDealerTypes = [];
      this.globalSearch = '';
      this.currentPage = 1;

      this.snackbarMessage = 'All filters cleared';
      this.snackbarColor = 'info';
      this.snackbar = true;

      this.applyFilters();
    },

    removeDealerCode(code) {
      const index = this.selectedDealerCodes.indexOf(code);
      if (index > -1) {
        this.selectedDealerCodes.splice(index, 1);
        this.currentPage = 1;
        this.applyFilters();
      }
    },

    removeDealerName(name) {
      const index = this.selectedDealerNames.indexOf(name);
      if (index > -1) {
        this.selectedDealerNames.splice(index, 1);
        this.currentPage = 1;
        this.applyFilters();
      }
    },

    removeDealerStatus(status) {
      const index = this.selectedDealerStatuses.indexOf(status);
      if (index > -1) {
        this.selectedDealerStatuses.splice(index, 1);
        this.currentPage = 1;
        this.applyFilters();
      }
    },

    removeArea(area) {
      const index = this.selectedAreas.indexOf(area);
      if (index > -1) {
        this.selectedAreas.splice(index, 1);
        this.currentPage = 1;
        this.applyFilters();
      }
    },

    removeAssignCompany(company) {
      const index = this.selectedAssignCompanies.indexOf(company);
      if (index > -1) {
        this.selectedAssignCompanies.splice(index, 1);
        this.currentPage = 1;
        this.applyFilters();
      }
    },

    removeDealerType(type) {
      const index = this.selectedDealerTypes.indexOf(type);
      if (index > -1) {
        this.selectedDealerTypes.splice(index, 1);
        this.currentPage = 1;
        this.applyFilters();
      }
    },

    applyFilters() {
      this.$forceUpdate();
    },

    matchesGlobalSearch(item, searchTerm) {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();

      return (
        (item.dealer_code && item.dealer_code.toLowerCase().includes(term)) ||
        (item.dealer_name && item.dealer_name.toLowerCase().includes(term)) ||
        (item.company_name && item.company_name.toLowerCase().includes(term)) ||
        (item.address && item.address.toLowerCase().includes(term)) ||
        (item.area_code && item.area_code.toLowerCase().includes(term)) ||
        (item.mobile_number && this.formatMobileNumber(item.mobile_number).toLowerCase().includes(term)) ||
        (item.mobile_number2 && this.formatMobileNumber(item.mobile_number2).toLowerCase().includes(term)) ||
        (item.dealer_status && item.dealer_status.toLowerCase().includes(term))
      );
    },

    formatMobileNumber(mobile) {
      if (!mobile) return '';
      if (mobile.startsWith('0')) {
        return mobile;
      }
      if (mobile.length === 10 && mobile.startsWith('7')) {
        return '0' + mobile;
      }
      return mobile;
    },

    formatCurrency(value) {
      if (!value) return 'Rs 0.00';
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return value;
      return new Intl.NumberFormat('en-LK', {
        style: 'currency',
        currency: 'LKR',
        minimumFractionDigits: 2
      }).format(numValue).replace('LKR', 'Rs');
    },

    populateFilterOptions() {
      // FIXED: Dealer Codes - trim, filter empty, remove case-insensitive duplicates
      const codeMap = new Map();
      this.routes.forEach(route => {
        if (route.dealer_code && route.dealer_code.trim()) {
          const trimmed = route.dealer_code.trim();
          const key = trimmed.toLowerCase();
          if (!codeMap.has(key)) {
            codeMap.set(key, trimmed);
          }
        }
      });
      this.dealerCodeOptions = Array.from(codeMap.values()).sort();

      // FIXED: Dealer Names - trim, filter empty, remove case-insensitive duplicates
      const nameMap = new Map();
      this.routes.forEach(route => {
        if (route.dealer_name && route.dealer_name.trim()) {
          const trimmed = route.dealer_name.trim();
          const key = trimmed.toLowerCase();
          if (!nameMap.has(key)) {
            nameMap.set(key, trimmed);
          }
        }
      });
      this.dealerNameOptions = Array.from(nameMap.values()).sort();

      // FIXED: Areas - trim, filter empty, remove case-insensitive duplicates
      const areaMap = new Map();
      this.routes.forEach(route => {
        if (route.area_code && route.area_code.trim()) {
          const trimmed = route.area_code.trim();
          const key = trimmed.toLowerCase();
          if (!areaMap.has(key)) {
            areaMap.set(key, trimmed);
          }
        }
      });
      this.areas = Array.from(areaMap.values()).sort();

      // FIXED: Assign Companies - trim, filter empty, remove case-insensitive duplicates
      // Company names are already normalized when the data is loaded, so here
      // the map naturally merges the renamed company with any existing exact match.
      const companyMap = new Map();
      this.routes.forEach(route => {
        if (route.companies && Array.isArray(route.companies)) {
          route.companies.forEach(company => {
            if (company.company && company.company.trim()) {
              const trimmed = company.company.trim();
              const key = trimmed.toLowerCase();
              if (!companyMap.has(key)) {
                companyMap.set(key, trimmed);
              }
            }
          });
        }
      });
      this.assignCompanies = Array.from(companyMap.values()).sort();
    },

    async fetchDealers() {
      this.loading = true;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${this.baseURL}/api/getAllDealers`, {
          params: {
            page: 1,
            per_page: 1000
          },
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data.success) {
          this.routes = response.data.data.map(dealer => ({
            id: dealer.id,
            dealer_code: dealer.dealer_code,
            dealer_name: dealer.dealer_name,
            company_name: dealer.company_name,
            address: dealer.address,
            region_code: dealer.region_code,
            area_code: dealer.area_code,
            mobile_number: dealer.mobile_number,
            mobile_number2: dealer.mobile_number2,
            lat: dealer.lat,
            lng: dealer.lng,
            dealer_status: dealer.status || 'ALLOWED',
            // CHANGED: normalize each assigned company name on load so the
            // rename applies everywhere (table, filters, PDF, Excel).
            companies: (dealer.companies || []).map(company => ({
              ...company,
              company: this.normalizeCompanyName(company.company)
            })),
            dealer_type: dealer.dealer_type || 'Retail',
            created_at: dealer.created_at,
            updated_at: dealer.updated_at
          }));

          this.populateFilterOptions();

          this.snackbarMessage = 'Dealers loaded successfully';
          this.snackbarColor = 'success';
          this.snackbar = true;
        } else {
          throw new Error(response.data.message || 'Failed to load dealers');
        }
      } catch (error) {
        console.error('Error fetching dealers:', error);
        this.snackbarMessage = error.response?.data?.message || error.message || 'Error loading dealers';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.loading = false;
      }
    },

    async updateStates(item) {
      this.selectOS = item.dealer_status;
      this.updateDetails = {
        id: item.id,
        dealer_name: item.dealer_name,
        dealer_code: item.dealer_code,
        mobile_number: item.mobile_number ? this.formatMobileNumber(item.mobile_number) : 'No mobile number'
      };
      this.updateDialog = true;
    },

    async reminderSend(id) {
      this.statusUpdateLoading = true;
      try {
        const userId = localStorage.getItem('userId');
        const token = localStorage.getItem('token');

        const statusData = {
          status: this.selectOS,
          reason: this.reason,
          userId: userId,
        };

        const response = await axios.put(
          `${this.baseURL}/api/updateDealerStatus/${id}`,
          statusData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data && response.data.success) {
          await this.fetchDealers();
          this.updateDialog = false;
          this.reason = '';

          this.snackbarMessage = 'Dealer status updated successfully';
          this.snackbarColor = 'success';
        } else {
          throw new Error(response.data?.message || 'Failed to update dealer status');
        }
      } catch (error) {
        console.error('Error updating dealer status:', error);
        this.snackbarMessage = error.response?.data?.message || error.message || 'Error updating dealer status';
        this.snackbarColor = 'error';
      } finally {
        this.statusUpdateLoading = false;
        this.snackbar = true;
      }
    },

    confirmDelete(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    async deleteItem() {
      if (!this.itemToDelete) {
        console.error('No item selected for deletion');
        return;
      }

      this.deleteLoading = true;
      try {
        const token = localStorage.getItem('token');
        const dealerId = this.itemToDelete.id;

        const response = await axios.delete(`${this.baseURL}/api/deleteDealer/${dealerId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data && response.data.success) {
          this.routes = this.routes.filter(route => route.id !== dealerId);
          this.populateFilterOptions();

          if (this.paginatedFilteredRoutes.length === 0 && this.currentPage > 1) {
            this.currentPage--;
          }

          this.snackbarMessage = 'Dealer deleted successfully';
          this.snackbarColor = 'success';
        } else {
          throw new Error(response.data?.message || 'Failed to delete dealer');
        }

        this.deleteDialog = false;
        this.itemToDelete = null;

      } catch (error) {
        console.error('Error deleting dealer:', error);
        this.snackbarMessage = error.response?.data?.message || error.message || 'Error deleting dealer';
        this.snackbarColor = 'error';
      } finally {
        this.deleteLoading = false;
        this.snackbar = true;
      }
    },

    confirmDeleteAll() {
      if (this.filteredRoutes.length === 0) {
        this.snackbarMessage = 'No dealers found to delete.';
        this.snackbarColor = 'warning';
        this.snackbar = true;
        return;
      }
      this.deleteAllDialog = true;
    },

    async executeDeleteAll() {
      this.deleteAllLoading = true;
      try {
        const token = localStorage.getItem('token');

        const idsToDelete = this.filteredRoutes.map(route => route.id);

        const response = await axios.post(`${this.baseURL}/api/deleteAllDealers`, { ids: idsToDelete }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data && response.data.success) {
          this.routes = this.routes.filter(route => !idsToDelete.includes(route.id));
          this.populateFilterOptions();
          this.currentPage = 1;

          this.snackbarMessage = 'All selected dealers deleted successfully';
          this.snackbarColor = 'success';
        } else {
          throw new Error(response.data?.message || 'Failed to delete all dealers');
        }
      } catch (error) {
        console.error('Error deleting all dealers:', error);
        this.snackbarMessage = error.response?.data?.message || error.message || 'Error deleting all dealers';
        this.snackbarColor = 'error';
      } finally {
        this.deleteAllLoading = false;
        this.deleteAllDialog = false;
        this.snackbar = true;
      }
    },

    exportPDF() {
      const dataToExport = this.filteredRoutes;

      if (dataToExport.length === 0) {
        this.snackbarMessage = 'No data to export. Please adjust your search criteria.';
        this.snackbarColor = 'warning';
        this.snackbar = true;
        return;
      }

      const doc = new jsPDF('landscape');

      doc.setFontSize(16);
      doc.text('DEALER LIST REPORT', 14, 20);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

      const tableColumn = [
        'Dealer Code',
        'Dealer Name',
        'Company Name',
        'Address',
        'Mobile 1',
        'Mobile 2',
        'Area',
        'Status',
        'Assigned Companies',
        'Dealer Type'
      ];

      const tableRows = dataToExport.map(item => {
        const companyNames = item.companies && item.companies.length > 0
          ? item.companies.map(c => c.company).join(', ')
          : 'N/A';

        return [
          item.dealer_code || 'N/A',
          item.dealer_name || 'N/A',
          item.company_name || 'N/A',
          item.address || 'N/A',
          item.mobile_number ? this.formatMobileNumber(item.mobile_number) : 'N/A',
          item.mobile_number2 ? this.formatMobileNumber(item.mobile_number2) : 'N/A',
          item.area_code || 'N/A',
          item.dealer_status || 'N/A',
          companyNames,
          item.dealer_type || 'N/A'
        ];
      });

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 40,
        theme: 'grid',
        headStyles: {
          fillColor: [128, 0, 128],
          textColor: [255, 255, 255],
          fontSize: 8
        },
        bodyStyles: {
          fontSize: 7
        },
        styles: {
          cellPadding: 2
        },
        margin: { top: 40, left: 10, right: 10 }
      });

      const fileName = `dealer_list_${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(fileName);
      this.logDocumentDownload(fileName, 'pdf');
    },

    exportExcel() {
      try {
        const dataToExport = this.filteredRoutes;

        if (dataToExport.length === 0) {
          this.snackbarMessage = 'No data to export. Please adjust your search criteria.';
          this.snackbarColor = 'warning';
          this.snackbar = true;
          return;
        }

        const excelData = dataToExport.map(item => {
          const companyDetails = item.companies && item.companies.length > 0
            ? item.companies.map(c => `${c.company} (Credit: ${this.formatCurrency(c.credit_limit_type)}, Cash: ${this.formatCurrency(c.cash_limit_type)})`).join('; ')
            : 'N/A';

          return {
            'Dealer Code': item.dealer_code || 'N/A',
            'Dealer Name': item.dealer_name || 'N/A',
            'Company Name': item.company_name || 'N/A',
            'Address': item.address || 'N/A',
            'Mobile Number 1': item.mobile_number ? this.formatMobileNumber(item.mobile_number) : 'N/A',
            'Mobile Number 2': item.mobile_number2 ? this.formatMobileNumber(item.mobile_number2) : 'N/A',
            'Area': item.area_code || 'N/A',
            'Dealer Status': item.dealer_status || 'N/A',
            'Assigned Companies': companyDetails,
            'Dealer Type': item.dealer_type || 'N/A',
            'Created Date': item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A',
            'Updated Date': item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'N/A'
          };
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);

        const colWidths = [
          {wch: 15}, // Dealer Code
          {wch: 25}, // Dealer Name
          {wch: 25}, // Company Name
          {wch: 40}, // Address
          {wch: 15}, // Mobile 1
          {wch: 15}, // Mobile 2
          {wch: 12}, // Area
          {wch: 15}, // Dealer Status
          {wch: 60}, // Assigned Companies
          {wch: 15}, // Dealer Type
          {wch: 15}, // Created Date
          {wch: 15}  // Updated Date
        ];
        worksheet['!cols'] = colWidths;

        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:L1');
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const address = XLSX.utils.encode_cell({ r: 0, c: C });
          if (!worksheet[address]) continue;
          worksheet[address].s = {
            font: { bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "800080" }, patternType: "solid" },
            alignment: { horizontal: "center", vertical: "center" }
          };
        }

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Dealer List');

        const fileName = `dealer_list_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        this.logDocumentDownload(fileName, 'excel');

        this.snackbarMessage = 'Excel file exported successfully';
        this.snackbarColor = 'success';
        this.snackbar = true;

      } catch (error) {
        console.error('Error exporting to Excel:', error);
        this.snackbarMessage = 'Error exporting to Excel: ' + error.message;
        this.snackbarColor = 'error';
        this.snackbar = true;
      }
    },

    async logDocumentDownload(fileName, fileType) {
      try {
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId');

        if (!userId) {
          throw new Error('User ID not found');
        }

        await axios.post(`${this.baseURL}/api/log-document-download`, {
          user_id: userId,
          file_type: fileType,
          file_name: fileName,
          entity_area: 'Management'
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } catch (error) {
        console.error('Error logging download:', error);
      }
    },

    viewItem(item) {
      this.$router.push({
        path: '/view-dealer',
        query: { id: item.id }
      });
    },

    editItem(item) {
      this.$router.push({
        path: '/edit-dealer',
        query: { id: item.id }
      });
    },

    backPageRoute() {
      this.$router.push('/dashboard');
    }
  },

  watch: {
    routes: {
      handler() {
        this.populateFilterOptions();
      },
      deep: true
    },

    filteredRoutes: {
      handler() {
        if (this.currentPage > this.totalPages && this.totalPages > 0) {
          this.currentPage = this.totalPages;
        }
      },
      deep: true
    }
  },

  mounted() {
    this.permissions = getPermissions(0, "Module Name");
    this.checkAdministratorStatus();

    if (!this.permissions.read) {
      this.snackbarMessage = "Access Denied";
      this.snackbarColor = "error";
      this.snackbar = true;
      this.$router.push('/dashboard');
    }
    this.fetchDealers();
  },

  computed: {
    hasActiveFilters() {
      return this.selectedDealerCodes.length > 0 ||
             this.selectedDealerNames.length > 0 ||
             this.selectedDealerStatuses.length > 0 ||
             this.selectedAreas.length > 0 ||
             this.selectedAssignCompanies.length > 0 ||
             this.selectedDealerTypes.length > 0 ||
             this.globalSearch;
    },

    filteredRoutes() {
      let filtered = [...this.routes];

      if (this.globalSearch && this.globalSearch.trim()) {
        filtered = filtered.filter(route => this.matchesGlobalSearch(route, this.globalSearch.trim()));
      }

      if (this.selectedDealerCodes && this.selectedDealerCodes.length > 0) {
        filtered = filtered.filter(route =>
          route.dealer_code && this.selectedDealerCodes.includes(route.dealer_code)
        );
      }

      if (this.selectedDealerNames && this.selectedDealerNames.length > 0) {
        filtered = filtered.filter(route =>
          route.dealer_name && this.selectedDealerNames.includes(route.dealer_name)
        );
      }

      if (this.selectedDealerStatuses && this.selectedDealerStatuses.length > 0) {
        filtered = filtered.filter(route =>
          this.selectedDealerStatuses.includes(route.dealer_status)
        );
      }

      if (this.selectedAreas && this.selectedAreas.length > 0) {
        filtered = filtered.filter(route =>
          route.area_code && this.selectedAreas.includes(route.area_code)
        );
      }

      if (this.selectedAssignCompanies && this.selectedAssignCompanies.length > 0) {
        filtered = filtered.filter(route =>
          route.companies && route.companies.some(company =>
            this.selectedAssignCompanies.includes(company.company)
          )
        );
      }

      if (this.selectedDealerTypes && this.selectedDealerTypes.length > 0) {
        filtered = filtered.filter(route =>
          route.dealer_type && this.selectedDealerTypes.includes(route.dealer_type)
        );
      }

      return filtered;
    },

    paginatedFilteredRoutes() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredRoutes.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredRoutes.length / this.perPage);
    },

    paginationStart() {
      if (this.filteredRoutes.length === 0) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },

    paginationEnd() {
      const end = this.currentPage * this.perPage;
      return end > this.filteredRoutes.length ? this.filteredRoutes.length : end;
    }
  }
}
</script>

<style>
@import "../../styles/outlet_management/outlet_list.scss";

/* Additional styles for company chips */
.company-item {
  padding: 2px 0;
}

.company-chip {
  font-weight: 500;
  margin-bottom: 2px;
}

.company-details {
  padding-left: 4px;
  opacity: 0.8;
}
</style>